import { BaseApiService } from '@/shared/api/services/base.service';
import type { JsonApiResource } from '@/shared/api/services/base.service';

/**
 * Survey copy, owned by the API.
 *
 * Every visible string here comes from the server on purpose: WEB, Apple and
 * Android must ask the same question in the same words, and a new reason code
 * has to reach phones without an app store release.
 */
export interface AppRatingForm {
  title: string;
  subtitle: string;
  /**
   * One entry per star, in order. A list rather than a map keyed by score:
   * a PHP array keyed 1..5 serializes as a JSON list, so an index-based read
   * would shift every label down a star.
   */
  rating_labels: Array<{ value: number; label: string }>;
  low_rating_prompt: string;
  high_rating_prompt: string;
  reasons: Array<{ code: string; label: string }>;
  message_placeholder: string;
  message_placeholder_high: string;
  submit_label: string;
  dismiss_label: string;
  thanks_message: string;
  /** Scores at or below this collect reason codes. Server-owned. */
  low_rating_threshold: number;
}

export interface AppRatingAnswer {
  rating: number;
  reason_codes?: string[];
  message?: string;
}

interface AppRatingSubmitResponse {
  data: JsonApiResource<{ rating: number; message: string }>;
}

class AppRatingService extends BaseApiService {
  async getForm(): Promise<AppRatingForm> {
    const response = await this.httpGet<{
      data: JsonApiResource<AppRatingForm>;
    }>('/v1/app-rating/form');

    return response.data.attributes;
  }

  /**
   * Submit an answer. Returns the server's thank-you line so the dialog closes
   * with the server's wording rather than one this client invented.
   */
  async submit(answer: AppRatingAnswer): Promise<string> {
    const response = await this.post<AppRatingSubmitResponse>(
      '/v1/app-rating',
      {
        data: {
          type: 'app_rating',
          attributes: answer,
        },
      }
    );

    return response.data.attributes.message;
  }

  /**
   * "Later". Fire-and-forget: the survey is already closed on screen by the
   * time this runs, and a lost call only risks being asked again next week.
   */
  async snooze(): Promise<void> {
    try {
      await this.post<void>('/v1/app-rating/snooze');
    } catch {
      // Advisory — never surface. Worst case the prompt returns sooner.
    }
  }
}

export const appRatingService = new AppRatingService();
